import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface SyncStatus {
  last_status: string | null;
  last_attempt_at: string | null;
  last_success_at: string | null;
  record_count: number | null;
  duration_ms: number | null;
  last_error: string | null;
}

interface KeystoneAccountStatus {
  account_id: number;
  slug: string;
  name: string;
  customer_code: string;
  keynet_url: string | null;
  use_keystone_api: boolean;
  vendor: SyncStatus | null;
  gl: SyncStatus | null;
}

interface Props {
  accountId: number;
  accountLabel: string;
  onClose: () => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmtAge = (iso: string | null): string => {
  if (!iso) return 'never';
  const ago = Date.now() - new Date(iso).getTime();
  const mins = Math.round(ago / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

type RefreshScope = 'vendor' | 'gl' | 'all';

const KeystoneSyncModal: React.FC<Props> = ({ accountId, accountLabel, onClose }) => {
  const [row, setRow] = useState<KeystoneAccountStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshingScope, setRefreshingScope] = useState<RefreshScope | null>(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<KeystoneAccountStatus[]>(
        `${API_BASE_URL}/admin/keystone/status`,
        { headers: authHeaders() }
      );
      const match = res.data.find((r) => r.account_id === accountId) || null;
      setRow(match);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to load Keystone status';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Per 2026-08-25 meeting: split refresh into vendor-only / gl-only / both.
  // Vendor pulls are ~10s+ on large tenants (Liberty is 30k vendors); GL is
  // ~1s. Splitting the button lets the clerk pick the cheap one when they
  // only need one refreshed.
  const triggerRefresh = async (scope: RefreshScope) => {
    setRefreshingScope(scope);
    try {
      const suffix = scope === 'all' ? '' : `/${scope}`;
      const res = await axios.post(
        `${API_BASE_URL}/admin/keystone/refresh/${accountId}${suffix}`,
        {},
        { headers: authHeaders(), timeout: 120_000 }
      );
      const results: Array<{ resource: string; status: string; record_count?: number; error?: string }> =
        res.data?.results || [];
      const allOk = results.every((r) => r.status === 'success');
      if (allOk) {
        toast.success(
          `Refresh complete: ${results
            .map((r) => `${r.record_count} ${r.resource}s`)
            .join(', ')}`
        );
      } else {
        const failed = results.filter((r) => r.status !== 'success');
        toast.error(
          `Refresh had errors: ${failed.map((r) => `${r.resource}: ${r.error || r.status}`).join('; ')}`
        );
      }
      await loadStatus();
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Refresh failed';
      toast.error(msg);
    } finally {
      setRefreshingScope(null);
    }
  };

  const renderResource = (label: string, s: SyncStatus | null) => {
    if (!s) {
      return (
        <div className="text-sm text-gray-500">
          <strong className="dark:text-gray-200">{label}:</strong> never synced
        </div>
      );
    }
    const ok = s.last_status === 'success';
    return (
      <div className="text-sm">
        <strong className="dark:text-gray-200">{label}:</strong>{' '}
        <span className={ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
          {s.last_status}
        </span>
        {ok && s.record_count !== null && (
          <span className="text-gray-500 dark:text-gray-400">
            {' '}— {s.record_count.toLocaleString()} records, {fmtAge(s.last_success_at)}
            {s.duration_ms != null && ` (${(s.duration_ms / 1000).toFixed(1)}s)`}
          </span>
        )}
        {!ok && s.last_error && (
          <div className="text-xs text-red-600 dark:text-red-400 mt-0.5 font-mono">
            {s.last_error}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold dark:text-white">Keystone API Sync</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{accountLabel}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : !row ? (
            <div className="text-sm text-gray-500">
              No Keystone configuration for this account. Set
              <code className="mx-1 px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                settings.keystone_customer_code
              </code>
              first.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 text-sm flex-wrap">
                <span className="dark:text-gray-200">
                  <strong>Customer code:</strong>{' '}
                  <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                    {row.customer_code}
                  </code>
                </span>
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    row.use_keystone_api
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                      : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  use_keystone_api: {row.use_keystone_api ? 'on' : 'off'}
                </span>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 break-all">
                <strong className="dark:text-gray-300">KeyNet URL:</strong>{' '}
                {row.keynet_url ? (
                  <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                    {row.keynet_url}
                  </code>
                ) : (
                  <span className="text-amber-600 dark:text-amber-400">
                    not configured — set <code>keystone_keynet_url</code> in account settings
                  </span>
                )}
              </div>

              <div className="border-t dark:border-gray-700 pt-3 space-y-2">
                {renderResource('Vendor', row.vendor)}
                {renderResource('GL accounts', row.gl)}
              </div>

              <div className="border-t dark:border-gray-700 pt-3 flex justify-end gap-2 flex-wrap">
                <button
                  onClick={() => triggerRefresh('vendor')}
                  disabled={refreshingScope !== null}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 disabled:opacity-50"
                  title="Refresh vendor cache only (slower on large tenants)"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${refreshingScope === 'vendor' ? 'animate-spin' : ''}`} />
                  {refreshingScope === 'vendor' ? 'Refreshing vendors...' : 'Refresh Vendors'}
                </button>
                <button
                  onClick={() => triggerRefresh('gl')}
                  disabled={refreshingScope !== null}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 disabled:opacity-50"
                  title="Refresh GL cache only (fast)"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${refreshingScope === 'gl' ? 'animate-spin' : ''}`} />
                  {refreshingScope === 'gl' ? 'Refreshing GL...' : 'Refresh GL'}
                </button>
                <button
                  onClick={() => triggerRefresh('all')}
                  disabled={refreshingScope !== null}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50"
                  title="Refresh both vendor + GL"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${refreshingScope === 'all' ? 'animate-spin' : ''}`} />
                  {refreshingScope === 'all' ? 'Refreshing all...' : 'Refresh Both'}
                </button>
              </div>

              <p className="text-xs text-gray-400 mt-2">
                Pulls fresh vendor and GL data from Keystone. Nightly sync runs at
                2 AM UTC; use these buttons when onboarding a new tenant, after
                Keystone bulk changes, or to confirm a fix. GL is fast (~1s),
                vendor can take 10-30s on large tenants.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KeystoneSyncModal;
